import * as Yup from 'yup';
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import InputField from 'src/components/InputField';
import MultilineInputField from 'src/components/MultilineInputField';
import { Form, FormikProvider, useFormik } from 'formik';
import { api } from 'src/Api';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import CloseIcon from '@mui/icons-material/Close';

const AddEditSubProjectModal = ({ open, onClose, refresh, initialData, projectData }) => {
  const [saveLoading, setSaveLoading] = useState(false);

  const EditAddSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().required('Description is required')
  });

  const formik = useFormik({
    enableReinitialize: true, // ✅ Add this line
    initialValues: {
      name: initialData?.name || '',
      description: initialData?.description || ''
    },
    validationSchema: EditAddSchema,
    onSubmit: () => {
      handleSave();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  function handleSave() {
    setSaveLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    initialData?.id && formData.append('id', initialData?.id);
    projectData?.id && formData.append('project_id', projectData?.id);
    values?.name && formData.append('name', values?.name);
    values?.description && formData.append('description', values?.description);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.add_edit_sub_project, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          onClose();
          refresh();
          formik.resetForm();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.resetForm(); // Reset form on modal close
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ pb: 0, paddingLeft: 2, paddingRight: 2, paddingTop: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 0
          }}
        >
          <Typography
            sx={{ fontSize: 16, color: palette.primary.titledark, fontFamily: 'poppinsBold' }}
          >
            {initialData ? 'Edit Sub Project' : 'Add Sub Project'}
          </Typography>
          <IconButton
            onClick={() => {
              formik.resetForm(); // Reset form on modal close
              onClose();
            }}
          >
            <CloseIcon style={{ color: palette.primary.disabled, fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent>
            {/* Project Dropdown */}
            <InputField
              disabled={true}
              label="Project"
              type="text"
              value={projectData?.name}
              labelFontMedium
            />

            {/* Name Field */}
            <InputField
              name="name"
              label="Sub Project Name"
              type="text"
              placeholder=""
              {...getFieldProps('name')}
              error={touched.name && errors.name}
              helperText={touched.name && errors.name}
              labelFontMedium
            />

            {/* Description Field */}
            <MultilineInputField
              name="description"
              label={'Description'}
              height={70}
              backgroundColor={'#fff'}
              fontSize={12}
              type="text"
              marginBottom={'1rem'}
              placeholder=""
              fontFamily="poppinsRegular"
              value={values.description}
              rows={3}
              maxRows={8}
              labelFontMedium
              {...getFieldProps('description')}
              error={touched.description && errors.description}
              helperText={touched.description && errors.description}
            />
          </DialogContent>

          <DialogActions sx={{ mb: 0, mr: 2 }}>
            <Button
              onClick={() => {
                formik.resetForm(); // Reset form on close button click
                onClose();
              }}
              variant="outlined"
              sx={{
                ml: 1.5,
                color: palette.primary.pageButtonTitle,
                borderColor: palette.primary.pageButtonBorder,
                fontSize: 12,
                height: 30,
                fontFamily: 'poppinsRegular',
                borderRadius: 0.7,
                '&:hover': {
                  backgroundColor: palette.primary.inputborder,
                  borderColor: palette.primary.pageButtonBorder
                }
              }}
            >
              Cancel
            </Button>
            <LoadingButtonWrapper
              loading={saveLoading}
              type="submit"
              variant="contained"
              sx={{
                width: saveLoading ? 120 : 90,
                height: 30,
                fontSize: 12,
                borderRadius: 0.7,
                fontFamily: 'poppinsLight',
                letterSpacing: 0.3,
                transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
              }}
              title="Save"
            />
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

export default AddEditSubProjectModal;
